import { DefaultTooltipContentProps, LegendProps } from 'recharts';
import dayjs from 'dayjs';
import { RechartsNameType, RechartsValueType } from '../types';

export type ChartAxesFormatter = (value: RechartsValueType) => string;
export type ChartTooltipFormatter = DefaultTooltipContentProps<
  RechartsValueType,
  RechartsNameType
>['formatter'];

export type ChartTooltipLabelFormatter = DefaultTooltipContentProps<
  RechartsValueType,
  RechartsNameType
>['labelFormatter'];

export type ChartLegendFormatter = LegendProps['formatter'];
export interface FormatterFunctions {
  xAxisFormatter?: ChartAxesFormatter;
  yAxisFormatter?: ChartAxesFormatter;
  tooltipFormatter?: ChartTooltipFormatter;
  tooltipLabelFormatter?: ChartTooltipLabelFormatter;
  legendFormatter?: ChartLegendFormatter;
}

/**
 * Formats a date string to "DD/MM" format.
 * @param date - The date to format, expected as a string.
 * @returns Formatted date string or original input if invalid.
 * @example
 * formatDateAsDayMonth('2023-04-15') // Returns '15/04'
 * formatDateAsDayMonth('Invalid Date') // Returns 'Invalid Date'
 */
const formatDateAsDayMonth: ChartAxesFormatter = (date) => {
  if (typeof date === 'string') {
    const parsedDate = dayjs(date);
    return parsedDate.isValid() ? parsedDate.format('DD/MM') : date;
  }
  return String(date);
};

/**
 * Formats a number to a compact representation.
 * @param value - The number to format.
 * @returns Formatted number string or empty string if input is not a number.
 * @example
 * formatCompactNumber(1000) // Returns '1K'
 * formatCompactNumber(1500000) // Returns '1.5M'
 */
const formatCompactNumber: ChartAxesFormatter = (value) => {
  if (typeof value === 'number') {
    return Intl.NumberFormat('en', { notation: 'compact' }).format(value);
  }
  return '';
};

/**
 * Formats a date string to "Month Day" format (e.g., "April 15").
 * @param label - The date to format, expected as a string.
 * @returns Formatted date string or original input if invalid.
 * @example
 * formatDateAsMonthDayLong('2023-04-15') // Returns 'April 15'
 * formatDateAsMonthDayLong('Invalid Date') // Returns 'Invalid Date'
 */
const formatDateAsMonthDayLong: ChartTooltipLabelFormatter = (label) => {
  if (typeof label === 'string') {
    const parsedDate = dayjs(label);
    return parsedDate.isValid() ? parsedDate.format('MMMM D') : label;
  }
  return String(label);
};

export const defaultFormatters: Required<FormatterFunctions> = {
  xAxisFormatter: formatDateAsDayMonth,
  yAxisFormatter: formatCompactNumber,
  tooltipFormatter: (value, name) => [
    value === undefined ? undefined : formatCompactNumber(value),
    name as string,
  ],
  tooltipLabelFormatter: formatDateAsMonthDayLong,
  legendFormatter: (value) => `${value}`,
};

/**
 * Object containing chart formatting functions.
 * These can be used individually or as part of the defaultFormatters.
 */
export { formatDateAsDayMonth, formatCompactNumber, formatDateAsMonthDayLong };
